import React, { useEffect, useRef, useState } from 'react';
import { trackStep } from '../lib/attribution';
import './OpenBroFullFilm.css';

/**
 * Lecteur 16:9 de /v52 et /v53 (façon university.com). Il prend la largeur de son parent : la page décide du cadrage.
 * Au scroll : lecture muette en boucle, voile, bouton play. Premier clic : retour à 0 et son.
 * Ensuite : barre maison (play / pause, −10 s, +10 s, piste cliquable et glissable, temps m:ss).
 * La barre est cachée pendant la lecture. Un tap met en pause, l’affiche, et pose un bouton au centre pour reprendre.
 * Pas de contrôles natifs, clic droit bloqué. Tout est préfixé .obf : le play de la home ne bouge pas.
 * `onLive(true)` tant que la vidéo joue avec le son et reste à l'écran : la page masque alors
 * ses éléments fixes (toast, barre collante) qui recouvriraient la barre du lecteur.
 */

const SKIP_S = 10;

/** iPhone n'accepte la lecture automatique que si l'élément est muet et inline avant play(). */
function armMuted(video) {
  video.muted = true;
  video.defaultMuted = true;
  video.playsInline = true;
  video.setAttribute('muted', '');
  video.setAttribute('playsinline', '');
  video.setAttribute('webkit-playsinline', '');
}

function armSound(video) {
  video.muted = false;
  video.defaultMuted = false;
  video.removeAttribute('muted');
}

function inFrame(video) {
  const node = video.parentElement || video;
  const rect = node.getBoundingClientRect();
  return rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
}

/** 75 → « 1:15 ». */
function clock(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.floor(seconds);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

function Glyph({ name }) {
  const shapes = {
    play: <path d="M8 5.5v13l10.5-6.5z" />,
    pause: <path d="M7 5h3.5v14H7zM13.5 5H17v14h-3.5z" />,
    back: <><path className="obf-stroke" d="M11 5.5A7 7 0 1 1 5 12" /><path d="M11 2.5v6L6.8 5.5z" /></>,
    fwd: <><path className="obf-stroke" d="M13 5.5A7 7 0 1 0 19 12" /><path d="M13 2.5v6l4.2-3z" /></>,
  };
  return <svg className="obf-glyph" viewBox="0 0 24 24" aria-hidden="true">{shapes[name]}</svg>;
}

export default function OpenBroFullFilm({ src, poster, playLabel, labels, onLive }) {
  const videoRef = useRef(null);
  const trackRef = useRef(null);
  const draggingRef = useRef(false);
  const resumeRef = useRef(false);
  const startedAt = useRef(0);
  const soundRef = useRef(false);
  const videoMarks = useRef(new Set());
  const [on, setOn] = useState(false);
  soundRef.current = on;
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [inView, setInView] = useState(false);
  const [held, setHeld] = useState(false);

  // Changement de langue / src : garder le son si la vidéo était déjà lancée, sinon aperçu muet.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    videoMarks.current = new Set();
    setTime(0);
    setDuration(0);
    setHeld(false);
    setPlaying(false);
    video.preload = 'auto';

    if (soundRef.current) {
      video.loop = false;
      const playWithSound = () => {
        if (!soundRef.current) return;
        armSound(video);
        video.loop = false;
        video.play().catch(() => {});
      };
      video.addEventListener('loadeddata', playWithSound);
      video.addEventListener('canplay', playWithSound);
      if (video.readyState >= 2) playWithSound();
      return () => {
        video.removeEventListener('loadeddata', playWithSound);
        video.removeEventListener('canplay', playWithSound);
      };
    }

    let started = false;
    const playMuted = () => {
      if (soundRef.current || !inFrame(video)) return;
      armMuted(video);
      const pending = video.play();
      if (pending && typeof pending.then === 'function') {
        pending.then(() => { started = true; }).catch(() => {});
      }
    };
    armMuted(video);
    playMuted();
    video.addEventListener('loadeddata', playMuted);
    video.addEventListener('canplay', playMuted);
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) playMuted();
      else if (started && !soundRef.current) video.pause();
    }, { threshold: 0.01 });
    observer.observe(video.parentElement || video);
    const onGesture = () => playMuted();
    window.addEventListener('pointerdown', onGesture, { capture: true, passive: true });
    window.addEventListener('touchstart', onGesture, { capture: true, passive: true });
    const retries = [300, 1000, 2200].map((delay) => window.setTimeout(playMuted, delay));
    return () => {
      video.removeEventListener('loadeddata', playMuted);
      video.removeEventListener('canplay', playMuted);
      window.removeEventListener('pointerdown', onGesture, { capture: true });
      window.removeEventListener('touchstart', onGesture, { capture: true });
      observer.disconnect();
      retries.forEach((id) => window.clearTimeout(id));
    };
  }, [src]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: 0.3 });
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    onLive?.(on && inView);
  }, [on, inView, onLive]);

  const start = (event) => {
    const video = videoRef.current;
    if (!video) return;
    event.preventDefault();
    event.stopPropagation();
    startedAt.current = Date.now();
    event?.currentTarget?.blur?.();
    setHeld(false);
    setOn(true);
    soundRef.current = true;
    video.loop = false;
    if (video.currentTime > 0.05) video.currentTime = 0;
    armSound(video);
    const pending = video.play();
    if (pending && typeof pending.catch === 'function') pending.catch(() => {});
  };

  const toggle = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      setHeld(false);
      if (soundRef.current) armSound(video);
      video.play().catch(() => {});
      return;
    }
    video.pause();
    setHeld(true);
  };

  const seekTo = (seconds) => {
    const video = videoRef.current;
    if (!video || !duration) return;
    const next = Math.min(duration, Math.max(0, seconds));
    video.currentTime = next;
    setTime(next);
  };

  const ratioAt = (clientX) => {
    const rect = trackRef.current.getBoundingClientRect();
    return Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
  };

  // Pointer events : souris et doigt avec le même code. La capture garde le glissé même hors de la piste.
  const onTrackDown = (event) => {
    if (!duration) return;
    draggingRef.current = true;
    resumeRef.current = !videoRef.current.paused;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    seekTo(ratioAt(event.clientX) * duration);
  };
  const onTrackMove = (event) => {
    if (draggingRef.current) seekTo(ratioAt(event.clientX) * duration);
  };
  // Glisser jusqu'au bout termine la vidéo (pause) : si elle jouait et qu'on revient en arrière, elle repart.
  const onTrackUp = (event) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    const video = videoRef.current;
    if (resumeRef.current && video.paused && video.currentTime < duration - 0.25) video.play().catch(() => {});
  };
  const onTrackKey = (event) => {
    if (event.key === 'ArrowLeft') { event.preventDefault(); seekTo(time - 5); }
    if (event.key === 'ArrowRight') { event.preventDefault(); seekTo(time + 5); }
  };

  const progress = duration ? (Math.min(time, duration) / duration) * 100 : 0;

  return (
    <div className={`obf${on ? ' is-on' : ''}${held ? ' is-held' : ''}`} onContextMenu={(event) => event.preventDefault()}>
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        muted={!on}
        autoPlay
        loop={!on}
        controls={false}
        controlsList="nodownload noplaybackrate noremoteplayback nofullscreen"
        disablePictureInPicture
        disableRemotePlayback
        playsInline
        preload="auto"
        onContextMenu={(event) => event.preventDefault()}
        onTimeUpdate={(event) => {
          if (!draggingRef.current) setTime(event.currentTarget.currentTime);
          const node = event.currentTarget;
          if (!node.duration) return;
          const pct = (node.currentTime / node.duration) * 100;
          [[1, 'video_started'], [50, 'video_50'], [95, 'video_100']].forEach(([mark, step]) => {
            if (pct < mark || videoMarks.current.has(step)) return;
            videoMarks.current.add(step);
            trackStep(step);
          });
        }}
        onLoadedMetadata={(event) => setDuration(event.currentTarget.duration)}
        onPlay={() => { setPlaying(true); setHeld(false); }}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onClick={() => { if (on && Date.now() - startedAt.current > 800) toggle(); }}
      />
      {!on && <div className="obf-veil" aria-hidden="true" />}
      {!on && (
        <button type="button" className="obf-start" aria-label={playLabel} onClick={start}>
          <span><Glyph name="play" /></span>
        </button>
      )}
      {on && held && !playing && (
        <button type="button" className="obf-resume" aria-label={labels.play} onClick={toggle}>
          <span><Glyph name="play" /></span>
        </button>
      )}
      {on && (
        <div className="obf-bar">
          <button type="button" className="obf-btn" onClick={toggle} aria-label={playing ? labels.pause : labels.play}>
            <Glyph name={playing ? 'pause' : 'play'} />
          </button>
          <button type="button" className="obf-btn obf-skip" onClick={() => seekTo(time - SKIP_S)} aria-label={labels.back}>
            <Glyph name="back" /><b>10</b>
          </button>
          <button type="button" className="obf-btn obf-skip" onClick={() => seekTo(time + SKIP_S)} aria-label={labels.fwd}>
            <Glyph name="fwd" /><b>10</b>
          </button>
          <div
            ref={trackRef}
            className="obf-track"
            role="slider"
            tabIndex={0}
            aria-label={labels.progress}
            aria-valuemin={0}
            aria-valuemax={Math.round(duration) || 0}
            aria-valuenow={Math.round(time)}
            aria-valuetext={`${clock(time)} / ${clock(duration)}`}
            onPointerDown={onTrackDown}
            onPointerMove={onTrackMove}
            onPointerUp={onTrackUp}
            onPointerCancel={onTrackUp}
            onKeyDown={onTrackKey}
          >
            <span className="obf-rail"><i style={{ width: `${progress}%` }} /></span>
            <span className="obf-knob" style={{ left: `${progress}%` }} />
          </div>
          <span className="obf-time">{clock(time)} / {clock(duration)}</span>
        </div>
      )}
    </div>
  );
}
